import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { Loader2, Download } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { downloadCsv } from "../lib/download";
import { formatSeat } from "../components/SeatMap";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

const channelOf = (t) => (t.is_comp ? "Comp" : t.payment_mode === "manual" ? "Box office" : "Online");
const CHANNEL_STYLE = {
  "Online": "bg-[#eefafa] text-[#1fb0b0]",
  "Box office": "bg-amber-100 text-amber-700",
  "Comp": "bg-gray-100 text-gray-500",
};
const STATUS_STYLE = {
  valid: "text-[#16a34a]", checked_in: "text-[#16a34a]", pending: "text-[#8a97a8]", cancelled: "text-red-500",
};

export default function AdminTicketsTab({ businesses }) {
  const [tickets, setTickets] = useState(null);
  const [vendor, setVendor] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setTickets(null);
    const url = vendor === "all" ? "/admin/tickets" : `/admin/tickets?business_id=${vendor}`;
    api.get(url).then(({ data }) => setTickets(data.tickets)).catch(() => setTickets([]));
  };
  useEffect(() => { load(); }, [vendor]);

  const rows = useMemo(() => {
    let r = tickets || [];
    if (q.trim()) { const s = q.trim().toLowerCase(); r = r.filter((t) => [t.code, t.attendee_name, t.buyer_name, t.buyer_email, t.business_name, t.product_title].some((f) => (f || "").toLowerCase().includes(s))); }
    return r;
  }, [tickets, q]);

  const exportCsv = async () => {
    try { await downloadCsv(vendor === "all" ? "/admin/tickets.csv" : `/admin/tickets.csv?business_id=${vendor}`, "all-tickets.csv"); }
    catch { toast({ title: "Export failed", variant: "destructive" }); }
  };

  return (
    <div data-testid="admin-tickets-tab">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <select value={vendor} onChange={(e) => setVendor(e.target.value)} data-testid="admin-ticket-vendor-filter"
          className="rounded-full px-4 py-2 text-sm border border-gray-200 bg-white text-[#16243b] outline-none focus:border-[#2ec9c9]">
          <option value="all">All vendors</option>
          {(businesses || []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-ticket-search" placeholder="Search code, name, email or vendor…"
          className="flex-1 min-w-[220px] rounded-full px-4 py-2 text-sm border border-gray-200 outline-none focus:border-[#2ec9c9]" />
        <button onClick={exportCsv} data-testid="admin-tickets-export-csv" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2"><Download size={15} /> Export CSV</button>
      </div>

      {!tickets ? <Loader2 className="animate-spin text-[#2ec9c9]" size={30} /> : (
        <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Code", "Vendor", "Event", "Tier", "Attendee", "Buyer", "Channel", "Price", "Status"].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((t) => {
                const ch = channelOf(t);
                return (
                  <tr key={t.id} data-testid={`admin-ticket-row-${t.id}`} className="border-t border-gray-50">
                    <td className="px-5 py-3 font-mono text-[#16243b]">{t.code}</td>
                    <td className="px-5 py-3 text-[#16243b] font-medium">{t.business_name}</td>
                    <td className="px-5 py-3 text-[#5a6b80]">{t.product_title}<div className="text-xs text-[#8a97a8]">{t.event_date ? fmtDate(t.event_date) : ""}</div></td>
                    <td className="px-5 py-3 text-[#5a6b80]">{t.tier_name}{t.seat ? ` · ${formatSeat(t.seat)}` : ""}</td>
                    <td className="px-5 py-3 text-[#5a6b80]">{t.attendee_name || "—"}</td>
                    <td className="px-5 py-3"><div className="text-[#16243b]">{t.buyer_name || "—"}</div><div className="text-xs text-[#8a97a8]">{t.buyer_email}</div></td>
                    <td className="px-5 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${CHANNEL_STYLE[ch]}`}>{ch}</span></td>
                    <td className="px-5 py-3 text-[#16243b]">£{(t.amount_paid ?? t.original_amount ?? 0).toFixed(2)}</td>
                    <td className={`px-5 py-3 font-medium capitalize ${STATUS_STYLE[t.status] || "text-[#5a6b80]"}`}>{(t.status || "valid").replace("_", " ")}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-[#8a97a8]">No tickets found.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
